/**
 * Shared clinical-text parsing helpers.
 *
 * These generic string/array/sentence utilities are used by BOTH the Modern
 * Medicine and Ayurveda flows. They were extracted from ConsultationPage.tsx
 * so the Ayurveda module (ayurveda/utils/parseAyurveda.ts) can reuse them
 * without duplicating logic. Pure functions — safe to call anywhere.
 */

import type { InvestigationEntry } from '@/lib/types/clinicalText';
export type { InvestigationEntry };

/**
 * Sanitize backend-supplied clinical text HTML before rendering with
 * dangerouslySetInnerHTML. Backends may wrap key terms (symptoms, diagnoses)
 * in tags like <b> to highlight them. Removes dangerous elements/attributes
 * while preserving safe inline formatting.
 */
export function sanitizeClinicalHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc
    .querySelectorAll('script,iframe,object,embed,link,style,svg,math,img')
    .forEach((el) => el.remove());
  doc.querySelectorAll('*').forEach((el) => {
    for (const attr of Array.from(el.attributes)) {
      el.removeAttribute(attr.name);
    }
  });
  return doc.body.innerHTML;
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function getStringField(value: unknown): string | null {
  if (typeof value === 'string') return value.trim() ? value.trim() : null;
  if (value && typeof value === 'object' && 'value' in value) {
    return getStringField((value as { value?: unknown }).value);
  }
  return null;
}

/**
 * Coerce a single array element to a display string.
 * Strings pass through; objects (e.g. the backend's SymptomDetail
 * `{ symptom_name, ... }`) are reduced to their readable text field so the UI
 * never renders "[object Object]". Objects with no readable text yield "" and
 * are filtered out by the caller — no placeholder/static text is injected.
 */
export function coerceToText(item: unknown): string {
  if (typeof item === 'string') return item.trim();
  if (item && typeof item === 'object') {
    const obj = item as Record<string, unknown>;
    const candidate = obj.symptom_name ?? obj.name ?? obj.text ?? obj.title ?? obj.value;
    if (typeof candidate === 'string') return candidate.trim();

    // Viruddha Ahara / CDSS object fallback: format common object shapes instead
    // of leaking "[object Object]" into the UI.
    const food = obj.food ?? obj.combination ?? obj.item ?? obj.medication;
    const reason = obj.reason ?? obj.explanation ?? obj.clinical_interpretation;
    const status = obj.status ?? (obj.suspected === true ? 'Suspected' : undefined);
    const parts: string[] = [];
    if (typeof food === 'string' && food.trim()) parts.push(food.trim());
    if (typeof status === 'string' && status.trim()) parts.push(`(${status.trim()})`);
    if (typeof reason === 'string' && reason.trim()) parts.push(`— ${reason.trim()}`);
    return parts.join(' ').trim();
  }
  return '';
}

export function getArrayField(value: unknown): string[] {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map(coerceToText).filter(Boolean);
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
}

export function sentenceCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function splitSentences(...texts: Array<string | null | undefined>): string[] {
  return texts
    .filter((text): text is string => Boolean(text && text.trim()))
    .flatMap((text) => text.match(/[^.!?]+[.!?]?/g) ?? [text])
    .map((sentence) => sentence.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
}

export function uniqueItems(items: string[]): string[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = item
      .toLowerCase()
      .replace(/[^\w\s]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function extractMatchingSentences(
  texts: Array<string | null | undefined>,
  patterns: RegExp[],
  limit = 5
): string[] {
  return uniqueItems(
    splitSentences(...texts).filter((sentence) =>
      patterns.some((pattern) => pattern.test(sentence))
    )
  ).slice(0, limit);
}

/**
 * Split text into fine-grained clauses (on commas, semicolons, and sentence
 * boundaries) instead of whole sentences. Used to avoid pulling an entire
 * run-on narrative sentence into a section when only one clause is relevant.
 */
export function splitClauses(...texts: Array<string | null | undefined>): string[] {
  return texts
    .filter((text): text is string => Boolean(text && text.trim()))
    .flatMap((text) => text.split(/[;,]|(?<=[.!?])\s+/))
    .map((clause) => clause.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
}

/**
 * Returns true if a clause is a NEGATION of the given topic — e.g.
 * "no significant family history", "no family history reported",
 * "none", "not reported". Prevents negated mentions from being
 * miscategorised as positive findings.
 */
export function isNegatedClause(clause: string): boolean {
  const c = clause.toLowerCase();
  return (
    /\bno\b[^.]*\bhistory\b/.test(c) ||
    /\bno\b[^.]*\b(reported|significant|known|family|medical|medication|medications|medicine|medicines|current)\b/.test(
      c
    ) ||
    /\bnot\b[^.]*\b(reported|present|known|significant|taking)\b/.test(c) ||
    /\b(none|nil|n\/a|denies|denied)\b/.test(c) ||
    /\bno significant\b/.test(c) ||
    /\bno current\b/.test(c)
  );
}

/**
 * Clause-level matcher that (1) splits into clauses, (2) drops negated clauses,
 * (3) keeps clauses matching any pattern. Safer than extractMatchingSentences
 * for history-type fields where run-on narrative sentences would otherwise leak
 * unrelated content (diet, sleep, complaint) into the wrong section.
 */
export function extractMatchingClauses(
  texts: Array<string | null | undefined>,
  patterns: RegExp[],
  limit = 5
): string[] {
  return uniqueItems(
    splitClauses(...texts)
      .filter((clause) => !isNegatedClause(clause))
      .filter((clause) => patterns.some((pattern) => pattern.test(clause)))
  ).slice(0, limit);
}

export function cleanClinicalItem(item: string): string {
  return item
    .replace(/^\s*[-*•]\s*/, '')
    .replace(/^\s*\d+\.\s*/, '')
    .replace(/\s+/g, ' ')
    .replace(/\s+([,.;:])/g, '$1')
    .trim();
}

export function splitClinicalItems(text?: string | null): string[] {
  if (!text) return [];

  return text
    .replace(/\s+(?=\d+\.\s+[A-Z])/g, '\n')
    .split(/\n|(?<=[.!?])\s+(?=[A-Z])|;\s+/)
    .map(cleanClinicalItem)
    .filter(Boolean);
}

export function compactItems(items: string[], limit = 6): string[] {
  return uniqueItems(items.map(cleanClinicalItem).filter(Boolean)).slice(0, limit);
}

/**
 * Splits a comma- or semicolon-joined value (e.g. "Fever, chest pain, and
 * cough for 5 days", or "Sliced left index finger with continuous
 * bleeding; stiff neck on the left side with pain; fever") into individual
 * bullet items. Also strips a leading "and "/"&" conjunction
 * left on the final item by the comma split, and splits on newlines too
 * for hand-edited text. HTML line breaks (<br>, list/paragraph container
 * tags — opening or closing <li>/<p>/<div>/<ul>/<ol>) are normalized to
 * newlines first — some sources deliver a multi-line value as HTML (even
 * already-listified markup) rather than literal "\n" characters, and
 * without this an unsplit value renders as one run-on line with no space
 * between the joined sentences. Shared by every surface that renders Chief
 * Complaint or Assessment/Diagnosis as a bullet list — Case Sheet, Patient
 * Record, PDFs, and the printed prescription — so they stay in sync.
 */
export function splitCommaSeparatedText(value: string | undefined | null): string[] {
  const normalized = (value || '')
    .replace(/<\/?(li|p|div|ul|ol)\b[^>]*>|<br\s*\/?>/gi, '\n')
    // A trailing "&nbsp;"/non-breaking-space (U+00A0) or zero-width-space
    // (U+200B) only line -- padding some sources leave after the last <br> --
    // would otherwise survive as its own non-empty-looking item and render
    // as a blank bullet, so normalize all three to a plain space first.
    .replace(/&nbsp;|\u00A0|\u200B/gi, ' ');

  // When the normalized text already contains a real newline -- e.g. joined
  // from a native array by extractSessionField/getSessionFieldValue/
  // getFieldValue, where each line is already the backend's own discrete
  // item -- those line boundaries are trusted as authoritative and are NOT
  // also split on comma/semicolon within a line. Otherwise an item like
  // "Chest infection, likely pneumonia" (one diagnosis with its own comma)
  // would be torn into two bullets, swallowing any notes joined onto a
  // second line by the same callers. Only a genuinely single-line,
  // comma-joined value (the true legacy shape) still splits on commas/
  // semicolons -- only a comma followed by whitespace counts as a separator
  // there, since a comma with no trailing space is a thousands separator
  // inside a number (e.g. the Indian numbering style "2,50,000").
  const splitPattern = normalized.includes('\n') ? /\n+/ : /;\s*|,\s+/;

  return (
    normalized
      .split(splitPattern)
      .map((item) => item.trim())
      .filter(Boolean)
      .map((item) => item.replace(/^(and|&)\s+/i, ''))
      // The "and"/"&" strip above can itself leave an item empty (e.g. a
      // trailing lone "and" clause) -- filter those out too.
      .filter(Boolean)
  );
}

/**
 * Renders a comma-joined value (Chief Complaint, Assessment/Diagnosis) as
 * an HTML bullet list (one <li> per item), for read-only surfaces that
 * render stored HTML directly via dangerouslySetInnerHTML (e.g.
 * PatientRecordView's summary sections). Preserves any inline markup (like
 * <b>) the AI highlighted within an item. Returns '' when there's nothing
 * to show, so callers can fall back to the original value/empty-message.
 */
export function commaTextToHtmlList(value: string | undefined | null): string {
  const items = splitCommaSeparatedText(value);
  if (items.length === 0) return '';
  return `<ul>${items.map((item) => `<li>${item}</li>`).join('')}</ul>`;
}

/**
 * Placeholder label the AI's structured exam entries sometimes carry instead
 * of a real system/finding name (e.g. "exam: mild murmur" rather than "Heart:
 * mild murmur") — it names the entry's kind, not anything informative to show
 * a reader, so splitLabelValueItem treats it as if there were no label.
 */
const GENERIC_EXAM_LABELS = new Set(['exam']);

/**
 * Splits a single bullet item on its first ":" into a { label, value } pair,
 * e.g. "Heart: mild murmur" -> { label: "Heart", value: "mild murmur" }. Used
 * for fields like General Examination, where each item is itself a labelled
 * "system/finding: detail" pair (flattened from the AI's structured exam
 * entries) rather than a plain phrase. Returns a null label when there's no
 * ":" (or it's at the very start/end) or the label is a generic placeholder
 * (see GENERIC_EXAM_LABELS), so the item renders unlabelled.
 */
export function splitLabelValueItem(item: string): { label: string | null; value: string } {
  const colonIndex = item.indexOf(':');
  if (colonIndex > 0 && colonIndex < item.length - 1) {
    const label = item.slice(0, colonIndex).trim();
    const value = item.slice(colonIndex + 1).trim();
    if (GENERIC_EXAM_LABELS.has(label.toLowerCase())) {
      return { label: null, value };
    }
    return { label, value };
  }
  return { label: null, value: item };
}

/**
 * Renders a comma-joined "label: value" list (e.g. General Examination,
 * flattened from the AI's structured exam entries) as an HTML bullet list,
 * bolding each item's label the same way the Case Sheet view does. For
 * read-only surfaces that render stored HTML directly via
 * dangerouslySetInnerHTML (e.g. PatientRecordView's summary sections).
 */
export function labelValueTextToHtmlList(value: string | undefined | null): string {
  const items = splitCommaSeparatedText(value);
  if (items.length === 0) return '';
  return `<ul>${items
    .map((item) => {
      const { label, value: itemValue } = splitLabelValueItem(item);
      return label ? `<li><strong>${label}:</strong> ${itemValue}</li>` : `<li>${itemValue}</li>`;
    })
    .join('')}</ul>`;
}

/**
 * Shared "relation/condition" entry formatting — used for family-history-style
 * fields, which the AI agent captures as `{ relation, condition, notes? }`
 * objects (optionally wrapped in `{ value: [...] }`). Centralized so every
 * screen that displays family history (Patient Record summary, Case Sheet
 * form/preview) renders it identically: one numbered, labeled block per
 * person, instead of each place inventing its own text join.
 */
export interface RelationConditionEntry {
  relation?: unknown;
  condition?: unknown;
  notes?: unknown;
}

export function isRelationConditionItem(item: unknown): item is RelationConditionEntry {
  return typeof item === 'object' && item !== null && 'relation' in item && 'condition' in item;
}

export function isInvestigationItem(item: unknown): item is InvestigationEntry {
  return typeof item === 'object' && item !== null && 'test' in item;
}

export function toDisplayText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    return value.map(toDisplayText).filter(Boolean).join(', ');
  }
  if (typeof value === 'object' && 'value' in value) {
    return toDisplayText((value as Record<string, unknown>).value);
  }
  return '';
}

/** Format one entry as `Relation: X\nCondition: Y\nNotes: Z` (Notes line omitted when absent). */
export function formatRelationConditionEntry(item: RelationConditionEntry, index?: number): string {
  const relation = toDisplayText(item.relation);
  const condition = toDisplayText(item.condition);
  const notes = toDisplayText(item.notes);
  const prefix = index !== undefined ? `${index}. ` : '';
  const lines = [`${prefix}Relation: ${relation}`, `Condition: ${condition}`];
  if (notes) lines.push(`Notes: ${notes}`);
  return lines.join('\n');
}

/** Format a full list of entries as numbered, blank-line-separated blocks. */
export function formatRelationConditionList(items: RelationConditionEntry[]): string {
  return items.map((item, i) => formatRelationConditionEntry(item, i + 1)).join('\n\n');
}

/**
 * Produce one bullet-ready block per family member — no leading number, since
 * the caller's own bullet marker (•, <li>, etc.) takes its place. Handles both
 * shapes family_history can arrive in:
 *  - the raw { relation, condition, notes }[] the AI agent captures, still
 *    live/unsaved;
 *  - the flattened "1. Relation: X\nCondition: Y\n\n2. Relation: Z..." text
 *    formatRelationConditionList produces, which is what's actually left once
 *    a Case Sheet save has passed it through as a plain string field — by then
 *    the original array is gone, so re-splitting that text (and stripping its
 *    "N. " prefix) is the only way to recover per-person bullets.
 * Also handles a plain unstructured string (e.g. a doctor's free-text note)
 * by returning it as a single item.
 */
export function getFamilyHistoryItems(value: unknown): string[] {
  const unwrapped =
    value && typeof value === 'object' && !Array.isArray(value) && 'value' in (value as object)
      ? (value as { value?: unknown }).value
      : value;

  if (
    Array.isArray(unwrapped) &&
    unwrapped.length > 0 &&
    unwrapped.every(isRelationConditionItem)
  ) {
    return (unwrapped as RelationConditionEntry[]).map((entry) =>
      formatRelationConditionEntry(entry)
    );
  }

  const text = typeof unwrapped === 'string' ? unwrapped : toDisplayText(unwrapped);
  if (!text.trim()) return [];

  return text
    .split(/\n\s*\n/)
    .map((block) => block.replace(/^\s*\d+\.\s*/, '').trim())
    .filter(Boolean);
}

/**
 * Renders family_history as an HTML bullet list (one <li> per family member,
 * internal Relation/Condition/Notes line breaks preserved), for read-only
 * surfaces that render stored HTML directly via dangerouslySetInnerHTML (e.g.
 * PatientRecordView's summary sections). Mirrors commaTextToHtmlList's role
 * for Chief Complaint/Allergies.
 */
export function familyHistoryTextToHtmlList(value: unknown): string {
  const items = getFamilyHistoryItems(value);
  if (items.length === 0) return '';
  // Internal line breaks are converted to <br> rather than a "white-space:
  // pre-line" style, since sanitizeClinicalHtml (applied before rendering)
  // strips all attributes — a style attribute would never survive it.
  return `<ul>${items.map((item) => `<li>${item.replace(/\n/g, '<br>')}</li>`).join('')}</ul>`;
}

/**
 * Format one investigation entry as `Test — value unit` (e.g. "Hemoglobin — 12 g/dL"),
 * or just the test name when no result was given (ordered/reviewed only, no value/unit).
 */
export function formatInvestigationEntry(item: InvestigationEntry): string {
  const test = toDisplayText(item.test);
  const value = toDisplayText(item.value);
  const unit = toDisplayText(item.unit);
  const result = [value, unit].filter(Boolean).join(' ');
  if (!test) return result;
  return result ? `${test} — ${result}` : test;
}

interface DiagnosisEntry {
  diagnosis?: unknown;
  notes?: unknown;
}

/**
 * Format one diagnosis entry as "diagnosis — notes" (notes omitted when
 * absent). Shared by every surface that extracts assessment/diagnosis array
 * items — Case Sheet, Patient Record, and PDFs — so they all agree on how a
 * diagnosis object is flattened to text.
 */
export function formatDiagnosisEntry(item: DiagnosisEntry): string {
  const diagnosis = toDisplayText(item.diagnosis);
  const notes = toDisplayText(item.notes);
  return notes ? `${diagnosis} — ${notes}` : diagnosis;
}

interface PhysicalExamEntryLike {
  type?: unknown;
  value?: unknown;
  notes?: unknown;
}

export function isPhysicalExamItem(item: unknown): item is PhysicalExamEntryLike {
  return typeof item === 'object' && item !== null && 'type' in item && 'value' in item;
}

/** Format one physical_examination entry as `<strong>Type:</strong> value — notes`. */
export function formatPhysicalExamEntry(item: PhysicalExamEntryLike): string {
  const type = toDisplayText(item.type);
  const value = toDisplayText(item.value);
  const notes = toDisplayText(item.notes);
  if (!type && !value) return '';
  const label = type ? `<strong>${type}:</strong> ` : '';
  return notes ? `${label}${value} — ${notes}`.trim() : `${label}${value}`.trim();
}

/**
 * Format one already-discrete array item (one array element = one bullet) as
 * HTML for a single `<li>`, dispatching purely on the item's shape:
 *  - plain string/number -> used as-is (may already carry inline `<b>`);
 *  - { relation, condition, notes? } -> family-history block;
 *  - { diagnosis, notes? } -> "diagnosis — notes";
 *  - { type, value, notes? } -> "<strong>Type:</strong> value — notes" (physical exam);
 *  - { test, ... } -> investigation entry;
 *  - any other object -> best-effort flattened text (toDisplayText).
 * Returns '' for an item with nothing renderable.
 */
function formatBulletItem(item: unknown): string {
  if (typeof item === 'string') return item.trim();
  if (typeof item === 'number' || typeof item === 'boolean') return String(item);
  if (isRecord(item)) {
    if (isRelationConditionItem(item)) {
      return formatRelationConditionEntry(item).replace(/\n/g, '<br>');
    }
    if ('diagnosis' in item) {
      // formatDiagnosisEntry already appends notes itself.
      return formatDiagnosisEntry(item as DiagnosisEntry);
    }
    if (isPhysicalExamItem(item)) {
      return formatPhysicalExamEntry(item);
    }
    if (isInvestigationItem(item)) {
      return formatInvestigationEntry(item);
    }
    return toDisplayText(item);
  }
  return '';
}

/**
 * Render a summary field's RAW (unflattened) value directly as an HTML
 * bullet list, one `<li>` per array element — instead of flattening every
 * value to a single comma-joined string and re-splitting it on commas
 * afterwards (commaTextToHtmlList/labelValueTextToHtmlList), which corrupts
 * any element that itself contains a comma or semicolon (e.g. "Fever onset 3
 * days ago, worse in the evenings" would wrongly become two bullets).
 *
 * The modern backend schema sends most summary fields as `string[]` (one
 * complete bullet per element, occasionally with inline `<b>` highlighting),
 * and structured-object arrays for family_history, physical_examination, and
 * assessment_and_diagnosis — this renders all of them directly, correctly,
 * from the original array. A nested `{ value: [...] }` envelope is unwrapped
 * first. Returns '' when `rawValue` isn't an array (callers should fall back
 * to the legacy string-based helpers for a plain string value) or has no
 * renderable items.
 */
export function summaryBulletsToHtmlList(
  rawValue: unknown,
  options?: { forceBold?: boolean }
): string {
  const unwrapped =
    rawValue &&
    typeof rawValue === 'object' &&
    !Array.isArray(rawValue) &&
    'value' in (rawValue as object)
      ? (rawValue as { value?: unknown }).value
      : rawValue;

  if (!Array.isArray(unwrapped)) return '';

  const items = unwrapped.map(formatBulletItem).filter(Boolean);
  if (items.length === 0) return '';
  // Some fields (e.g. Chief Complaint) should always render every bullet bold,
  // regardless of whether the backend happened to wrap that particular item in
  // <b> — wrapping again when it already is is harmless (nested <b> still just
  // renders bold).
  const rendered = options?.forceBold ? items.map((item) => `<b>${item}</b>`) : items;
  return `<ul>${rendered.map((item) => `<li>${item}</li>`).join('')}</ul>`;
}

/**
 * Matches an embedded lab/investigation clause inside a free-text history
 * narrative — e.g. "Lab investigations reviewed:", "Laboratory reports
 * reviewed:", "Blood test last month reviewed:", or "Blood work last
 * month:" (no "reviewed" word at all). Requires a lab/blood keyword, an
 * investigation/report/result/test/work noun, then optionally (loosely, up
 * to 4 words in between, e.g. "last month", with or without a trailing
 * "review(ed)") ending in a colon. Anchored via lookbehind to start only
 * right after a list/sentence boundary (start of string, or a
 * comma/period/semicolon + space) — without this, an incidental earlier
 * "Blood" (e.g. "Blood pressure" at the very start of the narrative) could
 * otherwise let the match span across several unrelated comma-separated
 * items before reaching the real marker further along. Case-insensitive,
 * captures nothing — only used to find the split point.
 */
const LAB_REVIEW_MARKER =
  /(?<=^|[,.;]\s)(?:lab(?:oratory)?|blood)\s+(?:investigations?|reports?|results?|tests?|work)(?:\s+\S+){0,4}?(?:\s+review(?:ed)?)?\s*:\s*/i;

/**
 * Splits a comma-separated list clause into individual bullet items. Only a
 * comma followed by whitespace is treated as a separator — a bare comma
 * (e.g. the Indian numbering style "2,50,000") stays intact as part of one
 * item. Collapses repeated internal whitespace and a stray double trailing
 * period (an artifact of splitClinicalItems' sentence boundary falling right
 * after an abbreviation/unit already ending in '.').
 */
function splitListClause(clause: string): string[] {
  return clause
    .replace(/\s+/g, ' ')
    .split(/,\s+/)
    .map((item) => item.trim().replace(/\.{2,}$/, '.'))
    .filter(Boolean);
}

/**
 * Splits a free-text, doctor-authored/AI-concatenated narrative (e.g. a Case
 * Sheet's Medical History field, built from several sentences joined
 * together) into individual bullet-ready items instead of treating the
 * whole paragraph as one bullet:
 *  - sentence by sentence, via splitClinicalItems (safely ignores decimal
 *    points like "16.4" and abbreviation periods);
 *  - then each sentence's own comma-separated list items are split too
 *    (e.g. "Hypertension, dyslipidemia, asthma" -> 3 bullets), without
 *    breaking thousands-separated numbers like "2,50,000".
 *
 * When the narrative contains an embedded "Lab investigations reviewed:"
 * clause (see LAB_REVIEW_MARKER), everything after it is pulled into a
 * separate `labItems` list instead of `mainItems`, so callers can render it
 * under its own "Lab Investigations Reviewed:" sub-heading rather than
 * mixing lab values into the main bullet list.
 */
export function splitMedicalNarrativeToBullets(text: string | undefined | null): {
  mainItems: string[];
  labItems: string[];
} {
  if (!text || !text.trim()) return { mainItems: [], labItems: [] };

  const mainItems: string[] = [];
  const labItems: string[] = [];

  // When the backend's native array has already been flattened to a string by
  // joining each item with '\n' (see extractSessionField/getSessionFieldValue),
  // each line IS the correct, authoritative bullet boundary already — unlike
  // a genuinely unstructured single-blob narrative (the `else` branch below),
  // it must NOT be further split on its own internal commas, or an item like
  // "Stroke about two years back, admitted for five days" wrongly becomes two
  // bullets (and can even split an inline <b>...</b> tag in half). The
  // embedded lab-review clause is the one exception: the backend intentionally
  // crams multiple lab values into that single array item using commas, so
  // only that line's post-marker portion still gets comma-split.
  const isPreSplit = text.includes('\n');
  const lines = isPreSplit ? text.split('\n') : splitClinicalItems(text);

  for (const line of lines) {
    const sentence = isPreSplit ? cleanClinicalItem(line) : line;
    if (!sentence) continue;
    const match = sentence.match(LAB_REVIEW_MARKER);
    if (match && match.index !== undefined) {
      // Deliberately NOT trimmed before splitting — trimming would strip the
      // trailing whitespace right before the marker that splitListClause's
      // "comma must be followed by whitespace" rule needs to recognize that
      // boundary as a separator, leaving a stray comma stuck on the last item
      // (e.g. "...local anesthesia," instead of "...local anesthesia").
      // splitListClause already trims each resulting item individually.
      const before = sentence.slice(0, match.index);
      const after = sentence.slice(match.index + match[0].length);
      if (before.trim()) {
        if (isPreSplit) {
          mainItems.push(before.trim());
        } else {
          mainItems.push(...splitListClause(before));
        }
      }
      if (after.trim()) labItems.push(...splitListClause(after));
      continue;
    }
    if (isPreSplit) {
      mainItems.push(sentence);
    } else {
      mainItems.push(...splitListClause(sentence));
    }
  }

  return { mainItems, labItems };
}

/**
 * Splits ANY free-text narrative into individual bullet-ready items —
 * sentence by sentence (decimal-safe), then each sentence's own
 * comma-separated list items split too (without breaking thousands-separated
 * numbers like "2,50,000"). Same splitting rules as
 * splitMedicalNarrativeToBullets, minus the "Lab investigations reviewed:"
 * sub-list detection — for every OTHER prose field (Social History, Review
 * of Systems, Treatment Plan, Doctor's Notes, etc.) that should render as
 * bullets but has no special embedded-clause handling of its own. A plain
 * short phrase with no sentence/list structure still comes back as a single
 * one-item array, so callers can treat the result uniformly.
 *
 * When `text` has already been flattened from a real array by joining items
 * with '\n' (see extractSessionField/getSessionFieldValue), each line is
 * treated as an already-correct, authoritative bullet boundary and is NOT
 * further split on its own internal commas — only a genuinely unstructured
 * single-blob narrative (no newlines) gets the comma-splitting heuristic.
 */
export function splitProseToBullets(text: string | undefined | null): string[] {
  if (!text || !text.trim()) return [];
  if (text.includes('\n')) {
    return text
      .split('\n')
      .map((line) => cleanClinicalItem(line))
      .filter(Boolean);
  }
  const items: string[] = [];
  for (const sentence of splitClinicalItems(text)) {
    items.push(...splitListClause(sentence));
  }
  return items;
}

/**
 * Renders any free-text narrative as an HTML bullet list (one `<li>` per
 * sentence/list-comma item), for fields with no dedicated bullet helper of
 * their own. See splitProseToBullets. Returns '' when there's nothing to
 * show, so callers can fall back to the original value/empty-message.
 *
 * Text that is already an HTML list (e.g. Astha Sthaana Pareeksha, built as
 * `<ul><li>…</li></ul>`) is left alone — wrapping it again nests a list inside
 * a list item, which draws a second bullet on the first line, and splitting on
 * commas would cut through the markup.
 */
export function proseTextToHtmlList(value: string | undefined | null): string {
  if (value && /<(ul|ol)[\s>]/i.test(value)) return '';
  const items = splitProseToBullets(value);
  if (items.length === 0) return '';
  return `<ul>${items.map((item) => `<li>${item}</li>`).join('')}</ul>`;
}
